import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Stack, Button, CircularProgress, Chip, Box, IconButton } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { LedgerType } from "../../components/transactionLedger/types";
import Iconify from "../../../minimals/components/iconify/Iconify";
import { fCurrency } from "../../utils/formatNumber";
import { fMonthDayYearSlashNotation } from "../../utils/formatTime";

type Props = {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
};

export default function IncomingWiresModal({ open, onClose, orderNumber }: Props) {
  const [loading, setLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState<"toEarnest" | "fromEarnest" | null>(null);
  const [transactionLedgers, setTransactionLedgers] = useState<LedgerType[]>([]);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const processing = processingAction !== null;

  const getTransactions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/proxy/api/ledgers/gettransactions?orderNumber=${orderNumber}`);
      const filtered = (data as LedgerType[]).filter(
          (t) => (t.typeDescription?.toLowerCase().includes("wire") 
              || t.typeDescription?.toLowerCase().includes("check")) 
              && t.isDebit === false
      );

      setTransactionLedgers(filtered);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getTransactions();
      setRowSelection({});
    }
  }, [open]);

  const selectedTransactionIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const selectedTransactions = useMemo(
      () => selectedTransactionIds
          .map((id) => transactionLedgers.find((t) => t.id === id))
          .filter((t): t is LedgerType => Boolean(t)),
      [selectedTransactionIds, transactionLedgers]
  );

  const selectedToEarnestMoney = useMemo(
      () => selectedTransactions.filter((t) => !t.isAppliedToEarnestMoney),
      [selectedTransactions]
  );

  const selectedFromEarnestMoney = useMemo(
      () => selectedTransactions.filter((t) => t.isAppliedToEarnestMoney),
      [selectedTransactions]
  );

  const moveTransactions = async (transactions: LedgerType[], endpoint: string, action: "toEarnest" | "fromEarnest") => {
    if (transactions.length === 0) return;

    setProcessingAction(action);

    try {
      for (const transaction of transactions) {
        await axios.post(
            endpoint,
            {
              transactionId: transaction.id,
              ledgerName: orderNumber
            }
        );
      }

      await getTransactions();

      setRowSelection({});
    } finally {
      setProcessingAction(null);
    }
  };

  const sendToEarnestMoney = async () => {
    await moveTransactions(
        selectedToEarnestMoney,
        "/proxy/api/ledgers/MoveTransactionAmountToEarnestMoney",
        "toEarnest"
    );
  };

  const moveOutOfEarnestMoney = async () => {
    await moveTransactions(
        selectedFromEarnestMoney,
        "/proxy/api/ledgers/MoveTransactionAmountFromEarnestMoneyToUnapplied",
        "fromEarnest"
    );
  };

  const columns = useMemo<MRT_ColumnDef<LedgerType>[]>(() => [
    {
      id: "earnestMoneyApplied",
      header: "Earnest Money",
      Header: () => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Earnest Money
          </Typography>
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
      enableColumnActions: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableHiding: false,
      Cell: ({ row }) =>
          row.original.isAppliedToEarnestMoney ? (
              <Box display="flex" justifyContent="center" alignItems="center">
                <Iconify icon="eva:checkmark-circle-fill" sx={{ color: "green" }} />
              </Box>
          ) : null
    },
    {
      header: "Status",
      accessorKey: "status",
      size: 110,
      maxSize: 120,
      Cell: ({ row }) => (
          <Chip
              label={row.original.status}
              color={
                row.original.status === "Pending"
                    ? "warning"
                    : row.original.status === "Posted"
                        ? "success"
                        : row.original.status === "Deleted"
                            ? "error"
                            : "default"
              }
              variant="soft"
              size="small"
              sx={{ borderRadius: "4px" }}
          />
      )
    },
    {
      header: "Transaction Type",
      accessorKey: "typeDescription",
      size: 180,
      maxSize: 220,
      Cell: ({ row }) => <Typography variant="body2">{row.original.typeDescription}</Typography>
    },
    {
      header: "In/Out",
      accessorKey: "isDebit",
      size: 70,
      maxSize: 80,
      Cell: ({ row }) => <Typography variant="body2">{row.original.isDebit ? "Out" : "In"}</Typography>
    },
    {
      header: "Tx Date",
      accessorKey: "transactionDate",
      size: 120,
      maxSize: 130,
      Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.transactionDate && fMonthDayYearSlashNotation(row.original.transactionDate)}
          </Typography>
      )
    },
    {
      header: "Amount",
      accessorKey: "amount",
      size: 120,
      maxSize: 130,
      Cell: ({ row }) => <Typography variant="body2">{fCurrency(row.original.amount)}</Typography>
    }
  ], []);

  return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Incoming Wires - {orderNumber}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                  variant="contained"
                  color="success"
                  onClick={moveOutOfEarnestMoney}
                  disabled={processing || selectedFromEarnestMoney.length === 0}
                  startIcon={processingAction === "fromEarnest" ? <CircularProgress size={20} /> : <Iconify icon="mdi:bank-transfer-out" />}
              >
                {processingAction === "fromEarnest" ? "Processing..." : "Not Earnest Money"}
              </Button>
              <Button
                  variant="contained"
                  color="primary"
                  onClick={sendToEarnestMoney}
                  disabled={processing || selectedToEarnestMoney.length === 0}
                  startIcon={processingAction === "toEarnest" ? <CircularProgress size={20} /> : <Iconify icon="mdi:bank-transfer" />}
              >
                {processingAction === "toEarnest" ? "Processing..." : "Send to Earnest Money"}
              </Button>
              <IconButton onClick={onClose} disabled={processing} size="small">
                <Iconify icon="mdi:close" width={22} />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <MaterialReactTable
              enableRowSelection
              getRowId={(row) => row.id}
              columns={columns}
              data={transactionLedgers}
              state={{ showSkeletons: loading, rowSelection }}
              onRowSelectionChange={setRowSelection}
              initialState={{
                density: "compact",
                pagination: { pageIndex: 0, pageSize: 30 }
              }}
              muiSelectCheckboxProps={({ row }) =>
                  row.original.isAppliedToEarnestMoney
                      ? {
                        color: "success",
                        sx: {
                          color: "success.main",
                          "&.Mui-checked": {
                            color: "success.main"
                          }
                        }
                      }
                      : {}
              }
              muiTableBodyRowProps={{
                sx: { opacity: 1, "&.Mui-disabled": { opacity: 1 } }
              }}
              muiTableBodyProps={{
                sx: {
                  "& .MuiTableRow-root:hover .MuiTableCell-root": {
                    backgroundColor: "inherit !important"
                  }
                }
              }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={processing} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
  );
}
